import clsx from "clsx";
import { cloneDeep } from "lodash-es";
import { FC, useMemo } from "react";

import { CourseDetailCourseCodeView } from "./CourseDetailCourseCodeView";
import { TermsNavList } from "./TermsNavList";

import { TERM_ID_QUERY_PARAM } from "components/constants";
import { type CourseViewOption } from "components/menus/CourseViewOptionMenu";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useAppSelector } from "store/index";
import {
  selectCourseCodeCoursesByTerms,
  selectIsEditingOrgSettings,
  selectTerms,
} from "store/selectors";
import { sortByTermYearRegex } from "store/selectors/sorting";
import { useFindItemBySearchParamId } from "utils/hooks/use-find-item-by-search-param-id";

const TERMS_EVENT_PREFIX = "Home - Terms / ";

export const TermsView: FC<{ onOptionChange: (option: CourseViewOption) => void }> = ({
  onOptionChange,
}) => {
  const terms = useAppSelector(selectTerms);
  const canEdit = useAppSelector(selectIsEditingOrgSettings);
  const courseCodeCoursesByTerm = useAppSelector(selectCourseCodeCoursesByTerms);
  const isSmallScreen = !useIsMdOrLarger();

  const orderedTerms = useMemo(() => {
    const termsToOrder = canEdit
      ? terms
      : terms.filter((term) => {
          const courseCodeCourses = courseCodeCoursesByTerm[term.id];
          return courseCodeCourses.some(
            (courseCode) => courseCode.courses && courseCode.courses.length > 0,
          );
        });

    return termsToOrder.toSorted(sortByTermYearRegex).map(cloneDeep);
  }, [terms, canEdit, courseCodeCoursesByTerm]);

  const currentTerm = useFindItemBySearchParamId(orderedTerms, TERM_ID_QUERY_PARAM, true);

  return (
    <div
      className={clsx(
        "flex h-full w-full items-start",
        isSmallScreen && "flex-col overflow-y-auto pb-4",
      )}
    >
      {(orderedTerms.length > 0 || canEdit) && (
        <TermsNavList canEdit={canEdit} terms={orderedTerms} onOptionChange={onOptionChange} />
      )}

      <main className={clsx("w-full", !isSmallScreen && "h-full flex-auto overflow-y-auto")}>
        {currentTerm && (
          <CourseDetailCourseCodeView
            canEdit={canEdit}
            eventPrefix={TERMS_EVENT_PREFIX}
            term={currentTerm}
          />
        )}
      </main>
    </div>
  );
};
